/**
 * TaskManagement — rewritten for workflow-stage-based task system
 *
 * Columns: title, site, workflow, current stage, progress, assignee, due date, status
 * Filters: site, status, workflow
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Button, Chip, Typography, LinearProgress,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Tooltip, Snackbar, Alert, ToggleButtonGroup, ToggleButton,
  CircularProgress,
} from '@mui/material';
import { Plus, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import BASE_URL from '../../config';
import { useOrg } from '../../contexts/OrgContext';
import { usePermissions } from '../../contexts/PermissionContext';
import ResponsiveTable from '../UI/ResponsiveTable';
import UnifiedTaskModal from './UnifiedTaskModal';
import CreateTaskDialog from './CreateTaskDialog';
import TaskBoard from './TaskBoard';

const STATUS_COLORS = {
  active: 'info',
  completed: 'success',
  cancelled: 'error',
};

const STAGE_STATUS_COLORS = {
  in_progress: 'warning',
  pending: 'default',
  completed: 'success',
  skipped: 'default',
};

const ProgressBar = ({ completed, total }) => {
  if (!total) return null;
  const pct = Math.round((completed / total) * 100);
  return (
    <Tooltip title={`${completed}/${total} stages`}>
      <Box sx={{ minWidth: 80 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{completed}/{total}</Typography>
        </Box>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} color={pct === 100 ? 'success' : 'primary'} />
      </Box>
    </Tooltip>
  );
};

const TaskManagement = () => {
  const { can } = usePermissions();
  const { selectedOrgId, loading: orgLoading } = useOrg();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [workflowFilter, setWorkflowFilter] = useState('all'); // workflow_template_id
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [maintenanceFilter, setMaintenanceFilter] = useState('all'); // 'all' | 'maintenance' | 'establishment'
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'board'
  const [boardStages, setBoardStages] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const fetchTasks = useCallback(async () => {
    if (!selectedOrgId || orgLoading) return;
    setLoading(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const params = new URLSearchParams({ organisation_id: selectedOrgId });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await axios.get(`${BASE_URL}/api/tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTasks(res.data || []);
    } catch (err) {
      console.error(err);
      showSnack('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, orgLoading, statusFilter]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Board view: fetch the selected workflow's ordered stages for the columns
  useEffect(() => {
    if (viewMode !== 'board' || workflowFilter === 'all' || !selectedOrgId) {
      setBoardStages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setBoardLoading(true);
      try {
        const token = await getAuth().currentUser?.getIdToken();
        const res = await axios.get(`${BASE_URL}/api/workflow-templates/${workflowFilter}`, {
          params: { organisation_id: selectedOrgId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setBoardStages((res.data?.stages || []).slice().sort((a, b) => a.position - b.position));
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) showSnack('Failed to load workflow stages', 'error');
      } finally {
        if (!cancelled) setBoardLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewMode, workflowFilter, selectedOrgId]);

  // Board drag-to-advance: complete the task's current stage (backend
  // validates required fields and auto-advances to the next stage)
  const handleAdvance = async (task) => {
    const cs = typeof task.current_stage === 'string' ? JSON.parse(task.current_stage) : task.current_stage;
    if (!cs?.id) return;
    try {
      const token = await getAuth().currentUser?.getIdToken();
      await axios.put(
        `${BASE_URL}/api/tasks/${task.id}/stages/${cs.id}?organisation_id=${selectedOrgId}`,
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnack(`"${cs.name}" completed`);
    } catch (err) {
      const data = err.response?.data;
      showSnack(
        data?.missing?.length
          ? `Required fields not filled: ${data.missing.join(', ')}`
          : (data?.error || 'Failed to advance stage'),
        'error'
      );
    } finally {
      fetchTasks();
    }
  };

  // Filter options derived from the loaded tasks
  const uniqueValues = (key) =>
    [...new Set(tasks.map(t => t[key]).filter(Boolean))].sort();
  const siteOptions = uniqueValues('site_name');
  const assigneeOptions = uniqueValues('assigned_to_name');
  const workflowOptions = useMemo(() => {
    const map = new Map();
    tasks.forEach(t => {
      if (t.workflow_template_id && t.workflow_name) map.set(t.workflow_template_id, t.workflow_name);
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  const filtered = tasks.filter(t => {
    if (siteFilter !== 'all' && t.site_name !== siteFilter) return false;
    if (workflowFilter !== 'all' && t.workflow_template_id !== workflowFilter) return false;
    if (assigneeFilter !== 'all') {
      if (assigneeFilter === '__unassigned__') {
        if (t.assigned_to_name) return false;
      } else if (t.assigned_to_name !== assigneeFilter) {
        return false;
      }
    }
    if (maintenanceFilter === 'maintenance' && !t.maintenance_plan_id) return false;
    if (maintenanceFilter === 'establishment' && t.maintenance_plan_id) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      t.title?.toLowerCase().includes(q) ||
      t.site_name?.toLowerCase().includes(q) ||
      t.workflow_name?.toLowerCase().includes(q) ||
      t.assigned_to_name?.toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>{row.title}</Typography>
          {row.site_name && <Typography variant="caption" color="text.secondary">{row.site_name}</Typography>}
        </Box>
      ),
    },
    {
      field: 'workflow_name',
      headerName: 'Workflow',
      renderCell: ({ row }) => row.workflow_name ? (
        <Chip label={row.workflow_name} size="small" variant="outlined" />
      ) : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'current_stage',
      headerName: 'Current Stage',
      sortable: false,
      renderCell: ({ row }) => {
        const cs = typeof row.current_stage === 'string' ? JSON.parse(row.current_stage) : row.current_stage;
        if (!cs) return <Chip label="All done" size="small" color="success" />;
        return (
          <Chip
            label={cs.name}
            size="small"
            color={STAGE_STATUS_COLORS[cs.status] || 'default'}
          />
        );
      },
    },
    {
      field: 'progress',
      headerName: 'Progress',
      sortable: false,
      renderCell: ({ row }) => (
        <ProgressBar
          completed={Number(row.completed_stages || 0)}
          total={Number(row.total_stages || 0)}
        />
      ),
    },
    {
      field: 'assigned_to_name',
      headerName: 'Assignee',
      renderCell: ({ row }) => (
        <Typography variant="body2">{row.assigned_to_name || '—'}</Typography>
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      renderCell: ({ row }) => {
        if (!row.due_date) return <Typography variant="caption" color="text.disabled">—</Typography>;
        const isOverdue = row.status === 'active' && new Date(row.due_date) < new Date();
        return (
          <Typography variant="body2" color={isOverdue ? 'error' : 'text.primary'}>
            {new Date(row.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: ({ row }) => (
        <Chip
          label={row.status?.charAt(0).toUpperCase() + row.status?.slice(1) || '—'}
          size="small"
          color={STATUS_COLORS[row.status] || 'default'}
        />
      ),
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      renderCell: ({ row }) => {
        if (!row.maintenance_plan_id) return <Typography variant="caption" color="text.disabled">—</Typography>;
        if (!row.outcome) return <Chip label="Pending" size="small" variant="outlined" />;
        const color = row.outcome === 'on_time' ? 'success' : row.outcome === 'delayed' ? 'warning' : 'error';
        const label = row.outcome === 'on_time' ? 'On time' : row.outcome.charAt(0).toUpperCase() + row.outcome.slice(1);
        return <Chip label={label} size="small" color={color} />;
      },
    },
  ];

  const canCreate = can('Task Management', 'taskAssignment', 'create');
  const canRead   = can('Task Management', 'taskAssignment', 'read');
  const canUpdate = can('Task Management', 'taskAssignment', 'update');

  if (!canRead) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">You do not have permission to view tasks.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Tasks</Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage all plantation workflow tasks.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<RefreshCw size={14} />} onClick={fetchTasks} variant="outlined">
            Refresh
          </Button>
          {canCreate && (
            <Button variant="contained" startIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
              New Task
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search tasks…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Site</InputLabel>
          <Select value={siteFilter} label="Site" onChange={e => setSiteFilter(e.target.value)}>
            <MenuItem value="all">All sites</MenuItem>
            {siteOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Workflow</InputLabel>
          <Select value={workflowFilter} label="Workflow" onChange={e => setWorkflowFilter(e.target.value)}>
            <MenuItem value="all">All workflows</MenuItem>
            {workflowOptions.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Assignee</InputLabel>
          <Select value={assigneeFilter} label="Assignee" onChange={e => setAssigneeFilter(e.target.value)}>
            <MenuItem value="all">All assignees</MenuItem>
            <MenuItem value="__unassigned__">Unassigned</MenuItem>
            {assigneeOptions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select value={maintenanceFilter} label="Type" onChange={e => setMaintenanceFilter(e.target.value)}>
            <MenuItem value="all">All tasks</MenuItem>
            <MenuItem value="establishment">Establishment</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, v) => {
            if (!v) return;
            setViewMode(v);
            // Convenience: auto-select the only workflow when entering board view
            if (v === 'board' && workflowFilter === 'all' && workflowOptions.length === 1) {
              setWorkflowFilter(workflowOptions[0].id);
            }
          }}
          sx={{ ml: 'auto' }}
        >
          <ToggleButton value="table" sx={{ textTransform: 'none', px: 2 }}>Table</ToggleButton>
          <ToggleButton value="board" sx={{ textTransform: 'none', px: 2 }}>Board</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'board' ? (
        workflowFilter === 'all' ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            Select a single workflow in the filter above to see its tasks as a stage board.
          </Alert>
        ) : boardLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : boardStages.length === 0 ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            This workflow has no stages defined.
          </Alert>
        ) : (
          <TaskBoard
            tasks={filtered}
            stages={boardStages}
            canUpdate={canUpdate}
            onAdvance={handleAdvance}
            onTaskClick={(task) => setSelectedTaskId(task.id)}
            onInvalidMove={(msg) => showSnack(msg, 'warning')}
          />
        )
      ) : (
      <ResponsiveTable
        data={filtered}
        columns={columns}
        loading={loading}
        onRowClick={(row) => setSelectedTaskId(row.id)}
        showSearch={false}
        emptyMessage={
          tasks.length === 0 && !loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary" gutterBottom>No tasks found.</Typography>
              {canCreate && (
                <Button variant="outlined" startIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                  Create your first task
                </Button>
              )}
            </Box>
          ) : 'No tasks match your filters.'
        }
      />
      )}

      {/* Task detail */}
      {selectedTaskId && (
        <UnifiedTaskModal
          open={!!selectedTaskId}
          taskId={selectedTaskId}
          onClose={() => setSelectedTaskId(null)}
          onUpdated={fetchTasks}
        />
      )}

      {/* Create task */}
      {createOpen && (
        <CreateTaskDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(newTaskId) => {
            setCreateOpen(false);
            fetchTasks();
            setSelectedTaskId(newTaskId);
            showSnack('Task created!');
          }}
        />
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskManagement;
